import { Link } from "react-router-dom";
import { ArrowLeft, HelpCircle } from "lucide-react";
import { SeoHead } from "@/lib/seo";
import { getFaqGroups } from "@/lib/seo-faq";
import { Breadcrumb, breadcrumbJsonLd } from "@/components/Breadcrumb";
import { useLang } from "@/lib/lang-context";

/**
 * Site-wide FAQ page (/faq). Renders a long list of questions grouped
 * by theme + the corresponding FAQPage JSON-LD so Google can show rich
 * Q&A snippets directly in the SERP.
 *
 * Bilingual: uses the active lang from context to pick IT / EN copy
 * end-to-end (groups, headers, CTA, meta). Schema.org FAQPage stays
 * localised so Google can index both variants distinctly.
 */
const COPY = {
  it: {
    metaTitle: "FAQ — Comprare e valutare casa nel Salento | Deal Rating",
    metaDesc:
      "Domande frequenti su valutazione immobili, OMI, vincoli paesaggistici, IMU, trulli, affitti turistici e investimenti immobiliari nel Salento.",
    breadcrumb: { home: "Home", faq: "FAQ" },
    eyebrow: "Centro domande",
    h1: "Domande frequenti",
    intro:
      "Tutto quello che ti serve sapere prima di valutare un annuncio immobiliare nel Salento: dai dati OMI ai vincoli del Piano Paesaggistico, dalle tasse di acquisto al rendimento degli affitti brevi.",
    tocLabel: "In questa pagina",
    ctaEyebrow: "Pronto a iniziare?",
    ctaTitle: "Analizza un annuncio in 20 secondi",
    ctaBody:
      "Incolla l'URL di un annuncio da Immobiliare, Idealista, Subito, Casa.it, Gate-away, Green-Acres, JamesEdition o LuxuryEstate e ottieni un report completo con punteggio, analisi OMI, stima IMU e segnalazione dei rischi.",
    ctaLabel: "Inizia gratis",
  },
  en: {
    metaTitle: "FAQ — Buying and evaluating property in Salento | Deal Rating",
    metaDesc:
      "Frequently asked questions on property valuation, OMI data, landscape constraints, IMU tax, trulli, short-let yield and real-estate investment in Salento.",
    breadcrumb: { home: "Home", faq: "FAQ" },
    eyebrow: "Question hub",
    h1: "Frequently asked questions",
    intro:
      "Everything you need to know before evaluating a Salento property listing: from OMI data to Landscape Plan constraints, from purchase taxes to short-let returns.",
    tocLabel: "On this page",
    ctaEyebrow: "Ready to start?",
    ctaTitle: "Analyse a listing in 20 seconds",
    ctaBody:
      "Paste the URL of any listing from Immobiliare, Idealista, Subito, Casa.it, Gate-away, Green-Acres, JamesEdition or LuxuryEstate and get a full report with score, OMI analysis, IMU estimate and risk flags.",
    ctaLabel: "Start free",
  },
};

const FAQ = () => {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.it;
  const groups = getFaqGroups(lang);
  const allItems = groups.flatMap((g) => g.items);
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage: lang === "en" ? "en" : "it",
    mainEntity: allItems.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
  const breadcrumbItems = [
    { label: c.breadcrumb.home, to: "/" },
    { label: c.breadcrumb.faq },
  ];

  return (
    <main data-testid="faq-page" className="bg-tufo">
      <SeoHead
        title={c.metaTitle}
        description={c.metaDesc}
        path="/faq"
        jsonLd={[faqSchema, breadcrumbJsonLd(breadcrumbItems)]}
      />

      <section className="max-w-3xl mx-auto px-5 md:px-10 pt-10 md:pt-16 pb-10 md:pb-12">
        <Breadcrumb items={breadcrumbItems} />
        <div className="label-eyebrow mt-6 mb-3 flex items-center gap-2">
          <HelpCircle size={12} strokeWidth={1.75} /> {c.eyebrow}
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#282C2A] leading-[1.05] tracking-tight">
          {c.h1}
        </h1>
        <p className="mt-5 md:mt-6 text-sm md:text-base text-[#282C2A] leading-relaxed max-w-2xl">
          {c.intro}
        </p>
      </section>

      {/* Table of contents */}
      <section className="max-w-3xl mx-auto px-5 md:px-10 pb-12">
        <div className="border-2 border-[#282C2A] bg-white p-6">
          <div className="label-eyebrow mb-3">{c.tocLabel}</div>
          <ul className="space-y-2">
            {groups.map((g) => (
              <li key={g.id}>
                <a
                  href={`#${g.id}`}
                  className="text-sm text-[#4A5D23] hover:underline"
                  data-testid={`faq-toc-${g.id}`}
                >
                  → {g.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Groups */}
      {groups.map((g) => (
        <section
          key={g.id}
          id={g.id}
          className="max-w-3xl mx-auto px-5 md:px-10 pb-12 scroll-mt-24"
          data-testid={`faq-group-${g.id}`}
        >
          <div className="label-eyebrow mb-3">{g.title}</div>
          <h2 className="font-serif-display text-3xl text-[#282C2A] mb-8 border-b border-[#E6E4DD] pb-3">
            {g.title}
          </h2>
          <div className="space-y-8">
            {g.items.map((it, i) => (
              <article key={i} data-testid={`faq-item-${g.id}-${i}`}>
                <h3 className="font-serif-display text-xl text-[#282C2A]">{it.q}</h3>
                <p className="text-sm text-[#282C2A] mt-2 leading-relaxed">{it.a}</p>
              </article>
            ))}
          </div>
        </section>
      ))}

      <section className="max-w-3xl mx-auto px-5 md:px-10 pb-20">
        <div className="border-2 border-[#4A5D23] bg-[#4A5D23] text-white p-6 md:p-8">
          <div className="label-eyebrow text-white/80 mb-2">{c.ctaEyebrow}</div>
          <h2 className="font-serif-display text-3xl">{c.ctaTitle}</h2>
          <p className="text-sm leading-relaxed mt-3 max-w-xl">{c.ctaBody}</p>
          <Link
            to="/"
            data-testid="faq-cta"
            className="mt-5 inline-block text-xs uppercase tracking-widest font-semibold bg-white text-[#4A5D23] px-4 py-2 hover:bg-[#F0EFEB] transition-colors"
          >
            {c.ctaLabel}
          </Link>
        </div>
      </section>
    </main>
  );
};

export default FAQ;
